import * as XLSX from "xlsx";

// Columnas EXACTAS que se requieren
const REQUIRED_COLUMNS = ["Codigo_Paso", "Fecha_liquidacion", "llave_liquidacion", "Tipo"];

const toStr = (v) => (v === undefined || v === null ? "" : String(v).trim());

const fechaReal = (dia, mes, anio) => {
  const d = Number(dia);
  const m = Number(mes);
  const y = Number(anio);
  const fecha = new Date(0);
  fecha.setUTCFullYear(y, m - 1, d);
  return fecha.getUTCFullYear() === y && fecha.getUTCMonth() === m - 1 && fecha.getUTCDate() === d
    ? `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`
    : null;
};

const normalizarFecha = (valor) => {
  if (valor === undefined || valor === null || valor === "") return null;

  if (valor instanceof Date && !isNaN(valor)) {
    return fechaReal(valor.getDate(), valor.getMonth() + 1, valor.getFullYear());
  }

  if (typeof valor === "number") {
    const fecha = XLSX.SSF.parse_date_code(valor);
    return fecha ? fechaReal(fecha.d, fecha.m, fecha.y) : null;
  }

  const s = String(valor).trim();
  if (!s) return null;

  const regex = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/;
  const match = s.match(regex);
  if (match) {
    let [, dd, mm, yy] = match;
    if (yy.length === 2) yy = `20${yy}`;
    return fechaReal(dd, mm, yy);
  }

  const iso = /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/;
  const isoMatch = s.match(iso);
  if (isoMatch) {
    const [, yy, mm, dd] = isoMatch;
    return fechaReal(dd, mm, yy);
  }

  return null;
};

/**
 * Normaliza el valor de Tipo.
 * Acepta: 1, "1", "01", "Tipo 1", "Tipo 01", 2, "2", "02", "Tipo 2", "Tipo 02".
 * Devuelve "1" o "2", o null si no es válido.
 */
const normalizarTipo = (valor) => {
  if (valor === undefined || valor === null || valor === "") return null;

  // Si es número directamente
  if (typeof valor === "number") {
    if (valor === 1) return "1";
    if (valor === 2) return "2";
    return null;
  }

  // String: limpiar y comparar
  const s = String(valor).trim().toLowerCase();
  // quitar prefijo "tipo" si existe y espacios
  const limpio = s.replace(/^tipo\s*/, "").replace(/^0+/, "") || s;

  if (limpio === "1") return "1";
  if (limpio === "2") return "2";

  return null;
};

export const validateExcelLote = async (file) => {
  const errors = [];
  const duplicatesInFile = [];

  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, {
      type: "array",
      cellDates: true,
      dateNF: "dd/mm/yy",
    });

    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      return {
        isValid: false,
        errors: ["El archivo no contiene ninguna hoja de cálculo."],
        totalRows: 0,
        validRows: 0,
        duplicatesInFile: [],
        resumenTipos: { tipo1: 0, tipo2: 0 },
      };
    }

    const worksheet = workbook.Sheets[sheetName];

    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      defval: "",
      blankrows: false,
      raw: false,
      dateNF: "dd/mm/yy",
    });

    if (!rows || rows.length === 0) {
      return {
        isValid: false,
        errors: ["El archivo está vacío o no tiene datos."],
        totalRows: 0,
        validRows: 0,
        duplicatesInFile: [],
        resumenTipos: { tipo1: 0, tipo2: 0 },
      };
    }

    // --- Validar encabezados ---
    const headers = (rows[0] || []).map((h) => toStr(h));
    const missingColumns = REQUIRED_COLUMNS.filter((col) => !headers.includes(col));

    if (missingColumns.length > 0) {
      errors.push(`Faltan las siguientes columnas obligatorias: ${missingColumns.join(", ")}`);
    }

    const extraColumns = headers.filter((h) => h && !REQUIRED_COLUMNS.includes(h));
    if (extraColumns.length > 0) {
      errors.push(
        `El archivo contiene columnas no permitidas: ${extraColumns.join(
          ", "
        )}. Solo se permiten: ${REQUIRED_COLUMNS.join(", ")}`
      );
    }

    if (missingColumns.length > 0 || extraColumns.length > 0) {
      return {
        isValid: false,
        errors,
        totalRows: rows.length - 1,
        validRows: 0,
        duplicatesInFile: [],
        resumenTipos: { tipo1: 0, tipo2: 0 },
      };
    }

    // --- Índices ---
    const idxCodigo = headers.indexOf("Codigo_Paso");
    const idxFecha = headers.indexOf("Fecha_liquidacion");
    const idxLlave = headers.indexOf("llave_liquidacion");
    const idxTipo = headers.indexOf("Tipo");

    const dataRows = rows.slice(1);
    if (dataRows.length === 0) {
      errors.push("El archivo debe contener al menos un registro además de los encabezados.");
    }
    let validRows = 0;
    const codigosVistos = new Map();
    let tipo1Count = 0;
    let tipo2Count = 0;

    dataRows.forEach((row, i) => {
      const rowNum = i + 2;
      const codigoRaw = row[idxCodigo];
      const fechaRaw = row[idxFecha];
      const llaveRaw = row[idxLlave];
      const tipoRaw = row[idxTipo];

      const codigo = toStr(codigoRaw);
      const fecha = toStr(fechaRaw);
      const llave = toStr(llaveRaw);
      const tipo = toStr(tipoRaw);

      // --- 1. Campos completos ---
      const vacios = [];
      if (!codigo) vacios.push("Codigo_Paso");
      if (!fecha) vacios.push("Fecha_liquidacion");
      if (!llave) vacios.push("llave_liquidacion");
      if (!tipo) vacios.push("Tipo");

      if (vacios.length > 0) {
        errors.push(`Fila ${rowNum}: campos vacíos en [${vacios.join(", ")}]`);
        return;
      }

      // --- 2. Tipo válido (1 o 2) ---
      const tipoNormalizado = normalizarTipo(tipoRaw);
      if (!tipoNormalizado) {
        errors.push(
          `Fila ${rowNum}: 'Tipo' debe ser 1 (conciliación diaria) o 2 (ajustes). Valor recibido: "${tipo}"`
        );
        return;
      }

      // --- 3. llave_liquidacion numérica ---
      if (!/^\d+(\.\d+)?$/.test(llave)) {
        errors.push(
          `Fila ${rowNum}: 'llave_liquidacion' debe ser numérico. Valor recibido: "${llave}"`
        );
        return;
      }

      // --- 4. Fecha válida ---
      const fechaNormalizada = normalizarFecha(fechaRaw);
      if (!fechaNormalizada) {
        errors.push(
          `Fila ${rowNum}: 'Fecha_liquidacion' con fecha o formato inválido. Use DD/MM/AA (ejemplo: 16/07/26). Valor recibido: "${fecha}"`
        );
        return;
      }

      // --- 5. Duplicados dentro del archivo ---
      if (codigosVistos.has(codigo)) {
        duplicatesInFile.push({ row: rowNum, value: codigo });
      } else {
        codigosVistos.set(codigo, rowNum);
      }

      // --- 6. Contadores por tipo ---
      if (tipoNormalizado === "1") tipo1Count++;
      if (tipoNormalizado === "2") tipo2Count++;

      validRows++;
    });

    if (duplicatesInFile.length > 0) {
      errors.push(
        `Se encontraron ${duplicatesInFile.length} código(s) Paso duplicados dentro del archivo.`
      );
    }

    const MAX_ERRORS = 50;
    const trimmedErrors =
      errors.length > MAX_ERRORS
        ? [...errors.slice(0, MAX_ERRORS), `... y ${errors.length - MAX_ERRORS} errores más`]
        : errors;

    return {
      isValid: trimmedErrors.length === 0 && duplicatesInFile.length === 0,
      errors: trimmedErrors,
      totalRows: dataRows.length,
      validRows,
      duplicatesInFile,
      resumenTipos: { tipo1: tipo1Count, tipo2: tipo2Count },
    };
  } catch (err) {
    console.error("Error al leer Excel:", err);
    return {
      isValid: false,
      errors: ["No se pudo leer el archivo Excel. Verifique que no esté corrupto."],
      totalRows: 0,
      validRows: 0,
      duplicatesInFile: [],
      resumenTipos: { tipo1: 0, tipo2: 0 },
    };
  }
};
